/** Grup çipinin sağ tık menüsü: renk, ad, katlama ve grubun tamamına yönelik işlemler. */
import { useTranslation } from 'react-i18next';
import { ChevronsLeftRight, Pencil, Ungroup, X } from 'lucide-react';
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
} from '@/shared/ui/ContextMenu';
import { cn } from '@/shared/lib/cn';
import { groupPalette, TAB_GROUP_COLOR_ORDER } from '../lib/tabGroupColors';
import type { TabGroup, TabGroupColor } from '../model/types';

export interface TabGroupContextMenuProps {
  group: TabGroup;
  onRenameStart: () => void;
  onColor: (color: TabGroupColor) => void;
  onToggleCollapsed: () => void;
  onUngroup: () => void;
  onCloseGroup: () => void;
}

export function TabGroupContextMenu({
  group,
  onRenameStart,
  onColor,
  onToggleCollapsed,
  onUngroup,
  onCloseGroup,
}: TabGroupContextMenuProps) {
  const { t } = useTranslation();

  return (
    <ContextMenuContent className="w-56">
      <ContextMenuLabel>{t('shell.tab.group.color')}</ContextMenuLabel>
      {/* Renkler menü öğesi değil tek satır: dokuz ayrı satır menüyü gereksiz uzatırdı. */}
      <div className="flex flex-wrap gap-1 px-2 pb-1.5">
        {TAB_GROUP_COLOR_ORDER.map((color) => (
          <button
            key={color}
            type="button"
            aria-label={t(`shell.tab.group.colors.${color}`)}
            title={t(`shell.tab.group.colors.${color}`)}
            aria-pressed={group.color === color}
            onClick={() => onColor(color)}
            className={cn(
              'h-5 w-5 rounded-full border-2',
              group.color === color ? 'border-fg' : 'border-transparent',
            )}
            style={{ backgroundColor: groupPalette(color).bg }}
          />
        ))}
      </div>
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={() => setTimeout(onRenameStart, 0)}>
        <Pencil className="h-4 w-4" />
        {t('shell.tab.group.rename')}
      </ContextMenuItem>
      <ContextMenuItem onSelect={onToggleCollapsed}>
        <ChevronsLeftRight className="h-4 w-4" />
        {group.collapsed ? t('shell.tab.group.expand') : t('shell.tab.group.collapse')}
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={onUngroup}>
        <Ungroup className="h-4 w-4" />
        {t('shell.tab.group.ungroup')}
      </ContextMenuItem>
      <ContextMenuItem onSelect={onCloseGroup}>
        <X className="h-4 w-4" />
        {t('shell.tab.group.close')}
      </ContextMenuItem>
    </ContextMenuContent>
  );
}
